import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { updateTeamSessionState } from '../store/slices/teamFocusSessionSlice';
import { TeamSessionState } from '../store/slices/teamFocusSessionSlice';

/**
 * Formats elapsed seconds into HH:MM format (no seconds shown).
 */
function formatElapsedHHMM(totalSeconds: number): string {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
}

/**
 * Tracks elapsed time for a team focus session.
 *
 * The elapsed time is computed from `sessionStartedAt` and updates every second.
 * Returns the formatted elapsed time in HH:MM format.
 */
export function useTeamSessionTimer(teamId: string | undefined, session: TeamSessionState | null): string {
    const dispatch = useDispatch();
    const [, setTick] = useState(0);
    const sessionRef = useRef(session);

    useEffect(() => {
        sessionRef.current = session;
    });

    useEffect(() => {
        const hasStarted = !!session?.sessionStartedAt;

        if (!teamId || !hasStarted) return;

        const interval = setInterval(() => {
            setTick(t => t + 1);
        }, 1000);

        return () => clearInterval(interval);
    }, [teamId, session?.sessionStartedAt, dispatch]);

    // Compute elapsed time from sessionStartedAt
    if (!session?.sessionStartedAt) return '00:00';

    const startTime = new Date(session.sessionStartedAt).getTime();
    const now = Date.now();
    const elapsedSeconds = Math.floor((now - startTime) / 1000);
    return formatElapsedHHMM(Math.max(elapsedSeconds, 0));
}
